import React, { useEffect, useState } from "react";
import "./projects.css";
import { PCB_CORE_URL } from "../config";
import CreateProjectModal from "./CreateProjectModal";
import { authenticatedFetch, clearAuthSession, getAccessToken } from "../authSession";

const API_PROJECTS = `${PCB_CORE_URL}/user/projects/`;

const STATUS_LABELS = {
  draft: "brouillon",
  validated: "validé",
  paid: "payé",
  en_cours: "en cours",
  expedie: "expédié",
  delivre: "délivré",
};

function statusLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "-";
  return STATUS_LABELS[raw.toLowerCase()] || raw;
}

function getErrorMessage(status, data) {
  if (data?.detail) return data.detail;
  if (data?.message) return data.message;

  if (status === 401) return "Session expirée. Veuillez vous reconnecter.";
  if (status === 403) return "Accès refusé.";
  if (status === 404) return "Endpoint introuvable (/user/projects/).";
  if (status >= 500) return "Erreur serveur. Réessayez dans quelques secondes.";
  return "Une erreur est survenue.";
}

export default function Projects() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [createError, setCreateError] = useState("");

  const token = getAccessToken();

  function handleLogout() {
    clearAuthSession();
    window.location.href = "/login";
  }

  async function loadProjects() {
    setLoading(true);
    setError("");

    try {
      const res = await authenticatedFetch(API_PROJECTS, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = getErrorMessage(res.status, data);

        if (res.status === 401) {
          setError(msg);
          setLoading(false);
          setTimeout(() => handleLogout(), 500);
          return;
        }

        throw new Error(msg);
      }

      const list = Array.isArray(data) ? data : data?.results || [];
      setItems(list);
    } catch (e) {
      setError(e?.message || "Erreur réseau. Vérifiez que le backend est accessible.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!token) {
      window.location.href = "/login";
      return;
    }
    loadProjects();
  }, [token]);

  async function createProject(title) {
    if (!token) {
      window.location.href = "/login";
      return;
    }

    setCreating(true);
    setCreateError("");

    try {
      const res = await authenticatedFetch(API_PROJECTS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = getErrorMessage(res.status, data);

        if (res.status === 401) {
          setCreateError(msg);
          setCreating(false);
          setTimeout(() => handleLogout(), 500);
          return;
        }

        throw new Error(msg);
      }
      setItems((prev) => [data, ...prev]);
      setModalOpen(false);
      window.location.href = `/app/projects/${data.id}/steps`;

    } catch (e) {
      setCreateError(e?.message || "Erreur lors de la création.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="projects-page">
      <div className="projects-card">
        <div className="projects-header">
          <div>
            <h1 className="projects-title">Mes projets</h1>
            <p className="projects-subtitle">Gérez vos projets et devis.</p>
          </div>

          <div className="projects-actions">
            <button
              className="projects-btn secondary"
              type="button"
              onClick={handleLogout}
              disabled={loading || creating}
            >
              Déconnexion
            </button>

            <button
              className="projects-btn"
              type="button"
              onClick={() => {
                setCreateError("");
                setModalOpen(true);
              }}
              disabled={loading || creating}
            >
              + Nouveau projet
            </button>
          </div>
        </div>

        {loading ? <div className="projects-info">Chargement...</div> : null}

        {/* ✅ Si GET en erreur => on montre juste l'erreur */}
        {error ? (
          <div className="projects-alert">
            {error}
            <div style={{ marginTop: 10 }}>
              <button
                className="projects-btn small secondary"
                type="button"
                onClick={loadProjects}
                disabled={loading || creating}
              >
                Réessayer
              </button>
            </div>
          </div>
        ) : null}

        {/* ✅ GET OK + empty => invite à créer */}
        {!loading && !error && items.length === 0 ? (
          <div className="projects-empty">
            Aucun projet pour l'instant. Cliquez sur “Nouveau projet”.
          </div>
        ) : null}

        {/* ✅ GET OK + list */}
        {!loading && !error && items.length > 0 ? (
          <div className="projects-list">
            {items.map((p) => (
              <div className="project-item" key={p.id}>
                <div className="project-main">
                  <div className="project-name">
                    {p.title || p.name || `Projet #${p.id}`}
                  </div>
                  <div className="project-meta">
                    Statut: {statusLabel(p.display_status || p.order_status || p.quote_status || p.status)}
                  </div>
                </div>

                <div className="project-right">
                  <button
                    className="projects-btn small"
                    type="button"
                    onClick={() => (window.location.href = `/app/projects/${p.id}/steps`)}

                  >
                    Ouvrir
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <CreateProjectModal
        open={modalOpen}
        onClose={() => {
          if (!creating) setModalOpen(false);
        }}
        onSubmit={createProject}
        loading={creating}
        error={createError}
      />
    </div>
  );
}
